import { useRef } from "react";

const groupThousands = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

interface AmountFieldProps {
  id: string;
  /** Plain decimal string ("3000.5"); integer and cents are edited separately. */
  value: string;
  onChange: (value: string) => void;
  centsLabel: string;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
  required?: boolean;
}

/** Catalog AmountBox: "$", big display integer and grey cents, no number spinners. */
export const AmountField = ({ id, value, onChange, centsLabel, invalid = false, describedBy, disabled, required }: AmountFieldProps) => {
  const decimalsRef = useRef<HTMLInputElement>(null);
  const [integerPart = "", decimalPart = ""] = value.split(".");
  const emit = (integer: string, decimals: string) => onChange(decimals ? `${integer}.${decimals}` : integer);

  return (
    <div
      className={`flex items-baseline justify-center gap-1 rounded-[var(--ds-radius-md)] border-[1.5px] bg-[var(--ds-surface-raised)] px-4 py-3 font-[var(--ds-font-display)] text-[48px] font-normal leading-[1.1] tracking-[-0.03em] tabular-nums focus-within:border-[var(--ds-ink)] ${
        invalid ? "border-[var(--ds-negative)] shadow-[inset_0_0_0_2px_var(--ds-negative)]" : "border-[var(--ds-ink-muted)]"
      } ${integerPart ? "text-[var(--ds-ink)]" : "text-[var(--ds-ink-muted)]"}`}
    >
      <span aria-hidden="true">$</span>
      <span className="relative inline-block">
        <span aria-hidden="true" className="invisible whitespace-pre">{groupThousands(integerPart) || "0"}</span>
        <input
          id={id}
          type="text"
          size={1}
          inputMode="numeric"
          autoComplete="off"
          value={groupThousands(integerPart)}
          onChange={(event) => {
            const raw = event.target.value;
            emit(raw.replace(/\D/g, "").replace(/^0+(?=\d)/, ""), decimalPart);
            if (raw.includes(".")) decimalsRef.current?.focus();
          }}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          placeholder="0"
          required={required}
          disabled={disabled}
          className="absolute inset-0 h-full w-full min-w-0 bg-transparent p-0 text-inherit outline-none placeholder:text-[var(--ds-ink-muted)] disabled:opacity-50"
        />
      </span>
      <span className="text-[22px] leading-none text-[var(--ds-ink-muted)]">
        <span aria-hidden="true">.</span>
        <input
          ref={decimalsRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={centsLabel}
          value={decimalPart}
          onChange={(event) => emit(integerPart, event.target.value.replace(/\D/g, "").slice(0, 2))}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && !decimalPart) document.getElementById(id)?.focus();
          }}
          placeholder="00"
          disabled={disabled}
          className="w-[2ch] bg-transparent p-0 text-inherit outline-none placeholder:text-[var(--ds-ink-muted)] focus:outline-none disabled:opacity-50"
        />
      </span>
    </div>
  );
};
